/** @format */

import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import PersonOutlineOutlined from '@mui/icons-material/PersonOutlineOutlined';
import PhoneIphoneOutlined from '@mui/icons-material/PhoneIphoneOutlined';
import WorkOutlineOutlined from '@mui/icons-material/WorkOutlineOutlined';
import {
  Box,
  Card,
  CardContent,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { FormikProps } from 'formik';
import React from 'react';
import { LegalGuardianDetailDto } from '../../Models';

interface Props {
  guardians: LegalGuardianDetailDto[] | undefined;
  formik: FormikProps<any> | undefined;
  // Index of the guardian currently being edited inline (null = none). The
  // matching card disables its delete button so the user can't accidentally
  // drop the only guardian while in the middle of amending it.
  editingIndex?: number | null;
  // Loads the clicked guardian's values into the inline edit form.
  onEdit?: (index: number) => void;
}
export default function DisplayGuardians({
  guardians,
  formik,
  editingIndex,
  onEdit,
}: Props) {
  const readOnly = !formik;

  return (
    <Grid container spacing={2}>
      {guardians &&
        guardians.map((rec: any, key: React.Key) => {
          const index = key as number;
          const isBeingEdited = editingIndex === index;
          return (
            <Grid item xs={12} md={6} lg={4} key={key}>
              <Card
                elevation={0}
                sx={{
                  minWidth: 0,
                  position: 'relative',
                  border: '1px solid',
                  borderColor: isBeingEdited ? 'warning.main' : 'divider',
                  borderLeft: '4px solid',
                  borderLeftColor: isBeingEdited ? 'warning.main' : 'primary.main',
                  borderRadius: 3,
                  minHeight: readOnly ? undefined : 160,
                  maxHeight: readOnly ? undefined : 160,
                  backgroundColor: isBeingEdited
                    ? (t) =>
                        t.palette.mode === 'light'
                          ? 'rgba(237, 108, 2, 0.08)'
                          : 'action.hover'
                    : readOnly
                      ? 'background.paper'
                      : 'action.hover',
                }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Stack direction='row' spacing={1.5} alignItems='flex-start'>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 2,
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: (t) =>
                          t.palette.mode === 'light'
                            ? 'rgba(25, 118, 210, 0.1)'
                            : 'action.selected',
                        color: 'primary.main',
                      }}>
                      <PersonOutlineOutlined />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{ fontSize: '1.05rem', fontWeight: 700, mb: 0.5 }}
                        color='text.primary'>
                        {`${rec.firstName} ${rec.surname}`}
                      </Typography>
                      <Typography
                        sx={{ mb: 0.5, fontWeight: 600 }}
                        variant='body2'
                        color='text.secondary'>
                        {rec.identityNumber}
                      </Typography>
                      <Stack direction='row' spacing={0.5} alignItems='center' sx={{ mb: 1 }}>
                        <PhoneIphoneOutlined
                          sx={{ fontSize: 18, color: 'text.secondary' }}
                        />
                        <Typography variant='body2' color='text.secondary'>
                          {rec.contactNumber}
                        </Typography>
                      </Stack>
                      {rec.employed && (
                        <Stack direction='row' spacing={0.75} alignItems='flex-start'>
                          <WorkOutlineOutlined
                            sx={{
                              fontSize: 20,
                              color: 'text.secondary',
                              mt: '2px',
                            }}
                          />
                          <Typography variant='body2' color='text.secondary'>
                            {`Works at ${rec.companyName} as a ${rec.occupation}.`}
                          </Typography>
                        </Stack>
                      )}
                    </Box>
                  </Stack>
                  {!readOnly && (
                    <Typography
                      variant='caption'
                      sx={{
                        display: 'block',
                        mt: 2,
                        color: 'text.secondary',
                      }}>
                      Review these details on the Guardian step if anything has changed.
                    </Typography>
                  )}
                  {isBeingEdited && (
                    <Typography
                      variant='caption'
                      sx={{
                        position: 'absolute',
                        bottom: 6,
                        left: 12,
                        color: '#ed6c02',
                        fontWeight: 600,
                      }}>
                      Editing... use the form above
                    </Typography>
                  )}
                  {formik && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        display: 'flex',
                      }}>
                      {onEdit && (
                        <Tooltip
                          title={
                            isBeingEdited
                              ? 'This guardian is being edited above'
                              : 'Edit guardian'
                          }>
                          <span>
                            <IconButton
                              disabled={isBeingEdited}
                              onClick={() => onEdit(index)}>
                              <EditIcon
                                color={isBeingEdited ? 'disabled' : 'primary'}
                              />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      <Tooltip
                        title={
                          isBeingEdited
                            ? 'Cancel the edit before deleting'
                            : 'Remove guardian'
                        }>
                        <span>
                          <IconButton
                            disabled={isBeingEdited}
                            onClick={() => {
                              const list = [...guardians];
                              const idx = list.findIndex(
                                (s: any) =>
                                  s.identityNumber === rec.identityNumber
                              );
                              if (idx !== -1) {
                                list.splice(idx, 1);
                                formik.setFieldValue('guardians', list);

                                if (list.length === 0) {
                                  formik.setFieldValue('dependants', []);
                                  formik.setFieldValue(
                                    'houseHoldIncomeDepandants',
                                    0
                                  );
                                }
                              }
                            }}>
                            <DeleteForeverIcon
                              color={isBeingEdited ? 'disabled' : 'error'}
                            />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
    </Grid>
  );
}
